import { handleVersionMismatch } from 'actions/versionSync';
import React from 'react';
import ReactModal from 'react-modal';
import { connect } from 'react-redux';
import { VersionMismatch } from 'services/BaseService';

import { IGateEvent } from "types/map";
import { beginGateEvent, chooseGateEventBranch, fetch as fetchMap, goBackFromGateEvent, } from 'actions/map';
import Loading from "components/Loading/index";

import Available from 'components/GateEventModal/Available';
import In from 'components/GateEventModal/In';
import End from 'components/GateEventModal/End';
import { IBranch, IEndStorylet, IStorylet } from "types/storylet";
import { IHasTypeString, IMessages, ISettingChangeMessage } from "types/app/messages";
import { fetchAvailable, goBack } from "actions/storylet/index";
import { processMessages } from "actions/app/index";
import { actionsUpdated } from 'actions/actions/index';
import { AREA_CHANGE_MESSAGE, SETTING_CHANGE_MESSAGE } from 'constants/messageTypes';
import processSettingChangeMessage from 'actions/app/processMessages/processSettingChangeMessage';
import ErrorState from './ErrorState';
import SecondChance from './SecondChance';
import StoryletService from 'services/StoryletService';
import { IChooseBranchRequestData } from 'types/storylet/service';

export interface Props {
  dispatch: Function,
  isBeingUpdated: boolean,
  isOpen: boolean,
  gateEvent: IGateEvent | undefined,
  onRequestClose: (shouldCloseMap?: boolean) => void,
}

export interface State {
  currentStep: SletStep,
  deferredSettingChangeMessage?: ISettingChangeMessage | undefined,
  didPlayerChangeArea: boolean,
  errorMessage?: string,
  isClosing: boolean,
  isGoingOnwards: boolean,
  isTryingAgain: boolean,
  messages?: IMessages | undefined,
  endStorylet?: IEndStorylet | undefined,
  secondChance?: IStorylet | undefined,
  storylet?: IStorylet | undefined,
}

enum SletStep {
  Available,
  In,
  End,
  SecondChance,
  Loading,
  Error,
}

export class GateEventModal extends React.Component<Props, State> {
  mounted = false;
  state: State = {
    currentStep: SletStep.Available,
    deferredSettingChangeMessage: undefined,
    didPlayerChangeArea: false,
    endStorylet: undefined,
    messages: undefined,
    isClosing: false,
    isGoingOnwards: false,
    isTryingAgain: false,
    storylet: undefined,
  };

  componentDidMount(): void {
    this.mounted = true;
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    // If the gate event has changed, it means that something has happened, and we need to update our
    // local state
    const { gateEvent } = this.props;
    if (prevProps.gateEvent === gateEvent) {
      return;
    }
    this.fetchAvailable();
  }

  componentWillUnmount(): void {
    this.mounted = false;
  }

  fetchAvailable = async () => {
    const { dispatch } = this.props;
    this.setState({ currentStep: SletStep.Loading });
    try {
      const { data } = await new StoryletService().fetchAvailable();
      // const { phase, storylet } = data as IFetchAvailableStoryletsResponseData;
      const { phase, storylet } = data;

      // We only know how to handle the In phase of a gate event
      if (phase === 'In') {
        this.setState({ storylet, currentStep: SletStep.In });
      }
    } catch (e) {
      if (e instanceof VersionMismatch) {
        dispatch(handleVersionMismatch(e));
        return;
      }
      throw e;
    }
  };

  handleBegin = async () => {
    const { dispatch, gateEvent } = this.props;
    if (!gateEvent) {
      return;
    }
    this.setState({ currentStep: SletStep.Loading });
    const { data: { isSuccess, message, storylet } } = await dispatch(beginGateEvent(gateEvent.id));
    if (isSuccess) {
      this.setState({ storylet }, () => {
        this.setState({ currentStep: SletStep.In });
      });
      return;
    }
    this.setState({ currentStep: SletStep.Error, errorMessage: message });
  };

  handleChooseBranch = async (branchData: IBranch & IChooseBranchRequestData) => {
    const { dispatch } = this.props;
    this.setState({ currentStep: SletStep.Loading });
    const { actions, endStorylet, messages, phase, secondChance } = await dispatch(chooseGateEventBranch(branchData));
    dispatch(actionsUpdated({ actions }));
    // Don't process setting change messages yet, but get it and defer it
    dispatch(processMessages(messages, [SETTING_CHANGE_MESSAGE]));
    const deferredSettingChangeMessage = messages?.defaultMessages?.find(({ type }: IHasTypeString) => type === SETTING_CHANGE_MESSAGE);
    const didPlayerChangeArea: boolean = !!(messages?.defaultMessages?.find((message: any) => message.type === AREA_CHANGE_MESSAGE));

    switch (phase) {
      case 'In':
        this.handleGoOnwardsFromEnd();
        return;
      case 'End':
        this.setState(({ storylet, ...state }) => ({
          ...state,
          deferredSettingChangeMessage,
          didPlayerChangeArea,
          endStorylet,
          messages,
          currentStep: SletStep.End,
        }));
        return;
      case 'SecondChance':
        this.setState(() => ({
          messages,
          secondChance,
          currentStep: SletStep.SecondChance,
        }));
        return;
      default:
        return;
    }
  };

  handleGoBackFromIn = () => {
    // Going back from the In phase means closing the modal
    this.handleRequestClose(false);
  };

  handleGoBackFromSecondChance = async () => {
    const { dispatch } = this.props;
    // We need to go back, then begin the gate event again
    this.setState({ currentStep: SletStep.Loading });
    await dispatch(goBack()); // This takes to the Available phase
    this.handleBegin(); // And now we can behave as though we've just started the gate event
  };

  handleGoBackFromEnd = async () => {
    this.setState({ currentStep: SletStep.Loading });
    await this.handleBegin();
  };

  handleGoOnwardsFromEnd = async () => {
    const { didPlayerChangeArea } = this.state;
    this.setState({ isGoingOnwards: true });
    this.handleRequestClose(didPlayerChangeArea);
    if (this.mounted) {
      // Cherry-pick stale deferred message out of state
      this.setState(({ deferredSettingChangeMessage, ...state }) => ({ ...state, isGoingOnwards: false }));
    }
  };

  handleOpen = () => {
    this.handleBegin();
  };

  cleanUpBeforeClosing = async () => {
    const { dispatch } = this.props;
    const {
      currentStep,
      deferredSettingChangeMessage,
    } = this.state;

    if (currentStep === SletStep.In || currentStep === SletStep.SecondChance) {
      dispatch(goBackFromGateEvent());
      return;
    }

    if (currentStep === SletStep.End) {
      if (deferredSettingChangeMessage) {
        dispatch(processSettingChangeMessage(deferredSettingChangeMessage));
      }
      await Promise.all([
        dispatch(fetchMap()),
        dispatch(fetchAvailable({ setIsFetching: true })),
      ]);
    }
  };

  handleRequestClose = async (shouldCloseMap?: boolean) => {
    const { onRequestClose } = this.props;
    const { currentStep, isClosing } = this.state;

    if (isClosing) {
      return;
    }

    // Nope. Wait for us to finish, please.
    if (currentStep === SletStep.Loading) {
      return;
    }

    this.setState({ isClosing });

    await this.cleanUpBeforeClosing();

    this.setState(({
      storylet,
      endStorylet,
      deferredSettingChangeMessage,
      ...state
    }) => ({
      ...state,
      currentStep: SletStep.Available,
      isClosing: false,
    }));

    onRequestClose(shouldCloseMap);
  };

  renderContent = () => {
    const { gateEvent, isBeingUpdated } = this.props;
    const {
      currentStep,
      endStorylet,
      errorMessage,
      isGoingOnwards,
      isTryingAgain,
      messages,
      secondChance,
      storylet,
    } = this.state;

    if (isBeingUpdated) {
      return <Loading spinner />;
    }

    switch (currentStep) {
      case SletStep.Available:
        return <Available gateEvent={gateEvent!} onClick={this.handleBegin} />;
      case SletStep.In:
        return <In
          storylet={storylet!}
          onGoBack={this.handleGoBackFromIn}
          onChooseBranch={this.handleChooseBranch}
        />;
      case SletStep.SecondChance:
        return (
          <SecondChance
            messages={messages}
            onChooseBranch={this.handleChooseBranch}
            onGoBack={this.handleGoBackFromSecondChance}
            secondChance={secondChance}
            storylet={storylet!}
          />
        );
      case SletStep.End:
        return <End
          endStorylet={endStorylet!}
          isGoingOnwards={isGoingOnwards}
          isTryingAgain={isTryingAgain}
          messages={messages!}
          onGoOnwards={this.handleGoOnwardsFromEnd}
          onTryAgain={this.handleGoBackFromEnd}
        />;
      case SletStep.Error:
        return <ErrorState message={errorMessage!} />;
      case SletStep.Loading:
      default:
        return <Loading spinner />;
    }
  };

  render() {
    let { gateEvent, isOpen } = this.props;

    if (!gateEvent) {
      return null;
    }

    return (
      <ReactModal
        overlayClassName="modal--tooltip-like__overlay modal--gate-storylet__overlay"
        className="modal--gate-storylet__content"
        isOpen={isOpen}
        onRequestClose={(_) => this.handleRequestClose()}
        style={{
          overlay: {
            touchAction: 'none',
          },
          content: {
            backgroundImage: 'https://images.fallenlondon.com/images/static/bg-paper-dark.png',
            touchAction: 'pan-x pan-y',
          },
        }}
        onAfterOpen={this.handleOpen}
      >
        {this.renderContent()}
      </ReactModal>
    );
  }
}

export default connect()(GateEventModal);
